<script setup lang="ts">
const { setPageSeo } = useSeo()

await setPageSeo({
    title: 'Conditions Générales de Vente',
    description: 'Conditions générales de vente des Ateliers d\'Été OXO Narbonne.'
})
</script>

<template>
    <div class="pt-32 pb-24 bg-slate-50 dark:bg-oxo-dark min-h-screen">
        <div class="container mx-auto px-6 max-w-4xl">
            <h1 class="font-display font-bold text-4xl mb-12 text-slate-900 dark:text-white">Conditions Générales de Vente</h1>

            <div class="prose prose-lg dark:prose-invert max-w-none space-y-8">

                <p class="lead text-xl text-gray-600 dark:text-gray-300">
                    Les présentes Conditions Générales de Vente (CGV) régissent l'achat de prestations proposées sur le site <strong>oxonarbonne.fr</strong> par l'École OXO Narbonne.
                </p>

                <section aria-labelledby="vendeur">
                    <h2 id="vendeur" class="text-2xl font-bold mb-4 text-oxo-red">1. Vendeur</h2>
                    <p>
                        OXO Narbonne – École de Jeux Vidéo et Cinéma d'Animation<br>
                        Pour toute question : <NuxtLink to="/contact" class="text-oxo-red hover:underline">Nous contacter</NuxtLink>
                    </p>
                </section>

                <section aria-labelledby="prestations">
                    <h2 id="prestations" class="text-2xl font-bold mb-4 text-oxo-red">2. Prestations proposées</h2>
                    <p>
                        Les présentes CGV s'appliquent exclusivement à l'achat des <strong>Ateliers d'Été</strong> proposés par OXO Narbonne.
                        Ces ateliers sont destinés aux jeunes à partir de 12 ans et se déroulent pendant les vacances d'été.
                    </p>
                </section>

                <section aria-labelledby="prix">
                    <h2 id="prix" class="text-2xl font-bold mb-4 text-oxo-red">3. Prix</h2>
                    <p>
                        Les prix sont indiqués en euros (€), toutes taxes comprises. Le prix applicable est celui affiché au moment de la commande.
                        OXO Narbonne se réserve le droit de modifier ses tarifs à tout moment, les prix étant applicables aux commandes passées après la modification.
                    </p>
                </section>

                <section aria-labelledby="commande">
                    <h2 id="commande" class="text-2xl font-bold mb-4 text-oxo-red">4. Processus d'inscription et de paiement</h2>
                    <p class="mb-4">
                        L'inscription s'effectue via le formulaire disponible sur la page <NuxtLink to="/ateliers-ete" class="text-oxo-red hover:underline">Ateliers d'Été</NuxtLink>.
                        Après validation du formulaire, le client est redirigé vers la plateforme de paiement sécurisé <strong>Stripe</strong>.
                    </p>
                    <p>
                        L'inscription n'est effective qu'après réception du paiement intégral et envoi d'un email de confirmation à l'adresse fournie.
                    </p>
                </section>

                <section aria-labelledby="annulation">
                    <h2 id="annulation" class="text-2xl font-bold mb-4 text-oxo-red">5. Annulation et remboursement</h2>
                    <p class="mb-4">
                        Conformément à l'article L.221-28 du Code de la consommation, le droit de rétractation de 14 jours ne s'applique pas aux prestations de formation ou de loisirs effectuées à une date ou une période déterminée.
                    </p>
                    <p class="mb-4">
                        En cas d'annulation par le client <strong>plus de 14 jours avant le début de l'atelier</strong>, un remboursement intégral sera effectué sous 10 jours ouvrés.
                        En cas d'annulation moins de 14 jours avant le début, aucun remboursement ne pourra être accordé, sauf cas de force majeure dûment justifié.
                    </p>
                    <p>
                        En cas d'annulation par OXO Narbonne (nombre minimum de participants non atteint, force majeure), les participants inscrits seront remboursés intégralement.
                    </p>
                </section>

                <section aria-labelledby="responsabilite">
                    <h2 id="responsabilite" class="text-2xl font-bold mb-4 text-oxo-red">6. Responsabilité</h2>
                    <p>
                        OXO Narbonne s'engage à mettre en œuvre tous les moyens nécessaires pour assurer la qualité des ateliers.
                        Sa responsabilité ne saurait être engagée en cas de force majeure ou d'événements indépendants de sa volonté.
                    </p>
                </section>

                <section aria-labelledby="donnees">
                    <h2 id="donnees" class="text-2xl font-bold mb-4 text-oxo-red">7. Données personnelles</h2>
                    <p>
                        Les informations collectées lors de l'inscription sont utilisées uniquement pour la gestion de votre inscription et le traitement du paiement.
                        Pour plus d'informations, consultez notre <NuxtLink to="/politique-confidentialite" class="text-oxo-red hover:underline">Politique de confidentialité</NuxtLink>.
                    </p>
                </section>

                <section aria-labelledby="litige">
                    <h2 id="litige" class="text-2xl font-bold mb-4 text-oxo-red">8. Litiges</h2>
                    <p>
                        En cas de litige, une solution amiable sera recherchée avant tout recours judiciaire.
                        À défaut, les tribunaux compétents du ressort du siège social d'OXO Narbonne seront seuls compétents.
                        Les présentes CGV sont soumises au droit français.
                    </p>
                    <p class="mt-4">
                        Conformément aux dispositions du Code de la consommation, vous pouvez recourir gratuitement au service de médiation
                        <strong>CM2C</strong> — 49 rue de Ponthieu, 75 008 PARIS — Tél : 01 89 47 00 14 —
                        <a href="https://www.cm2c.net/declarer-un-litige.php" target="_blank" rel="noopener noreferrer" class="text-oxo-red underline">www.cm2c.net</a> —
                        <a href="mailto:litiges@cm2c.net" class="text-oxo-red underline">litiges@cm2c.net</a>.
                    </p>
                </section>

                <p class="text-sm text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-6">
                    Dernière mise à jour : mars 2025
                </p>

            </div>
        </div>
    </div>
</template>
